<template>
  <div class="w-full mx-auto md:mt-8 mt-[24px]" ref="rootContainer">
    <TabView 
      v-model:activeIndex="activeTab" 
      class="bg-white" 
      :lazy="true"
    >
      <TabPanel 
        v-for="(tab, idx) in tabs" 
        :key="tab.key"
      >
        <template #header>
          <span :aria-label="tab.label">{{ tab.label }}</span>
        </template>
        <component
          :is="tab.component"
          v-if="tab.key === TAB_KEYS.PERSONAL"
          ref="personalInfoTabRef"
          @save-personal-info="onSavePersonalInfo"
          @go-to-subscription="goToSubscriptionTab"
        />
        <component
          :is="tab.component"
          v-else-if="tab.key === TAB_KEYS.SETTINGS"
          ref="settingsTabRef"
          @save-settings="onSaveSettings"
          @sync="emit('sync')"
        />
        <component
          :is="tab.component"
          v-else-if="tab.key === TAB_KEYS.SUBSCRIPTION"
          ref="subscriptionsTabRef"
          @sync="syncSubscription"
          @go-to-personal="goToPersonalTab"
        />
        <component :is="tab.component" v-else />
      </TabPanel>
    </TabView>
  </div>
  <div v-if="userStore.errorMessage || profileStore.errorMsg">
    <span
      class="text-base my-2 !font-just text-error flex justify-center"
      data-test-id="login-errorMessage"
    >
      {{ userStore.errorMessage || profileStore.errorMsg }}
    </span>
  </div>
  <div
    class="flex justify-center items-center flex-col w-full"
    v-if="showSaveBtn"
  >
    <BaseButton
      :label="$t('profileLabels.saveBtnText')"
      class="w-full"
      :disabled="!canSave"
      @click="onSave"
      :loading="userStore.loading"
    />
  </div>
  <div
    tabindex="0"
    @focus="handleFocusRedirect"
    class="opacity-0 h-0 w-0 pointer-events-none absolute"
  ></div>
</template>

<script setup>
import { computed, onMounted, ref, watch , defineAsyncComponent , nextTick } from "vue";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import { profileLabels } from "@/utils/label";
import { useUserStore } from "@/stores/userStore";
import { useProfileStore } from "@/stores/profileStore";
import { useI18n } from "vue-i18n";

// Emit events to parent component
// - "sync": Triggered when data needs to be synchronized
const emit = defineEmits(["sync"]);

// Access the global store instance
const userStore = useUserStore();
const profileStore = useProfileStore();
const { t } = useI18n();

// UI state for tab management
const activeTab = ref(0);

// Validation states for tabs that require form validation before saving
const personalInfoValid = ref(false);
const settingValid = ref(false);

// Template references for accessing child tab components
const personalInfoTabRef = ref(null);
const settingsTabRef = ref(null);

// Data objects to hold form data from different tabs before saving
let profileData = ref({});
let addressData = ref({});
let contactData = ref({});

// Enable save button when form is valid and dirty
const canSave = computed(() => {
  if (getTabKey(activeTab.value) === TAB_KEYS.PERSONAL) {
    return personalInfoValid.value;
  } else if (getTabKey(activeTab.value) === TAB_KEYS.SETTINGS) {
    return settingValid.value;
  }
  return true;
});

// Tab configuration with lazy-loaded components for better performance
const tabs = computed(() => [
  {
    key: "personal",
    label: t('profileLabels.personalInformationTab'),
    component: defineAsyncComponent(() => import("./PersonalInfoTab.vue")),
    ref: personalInfoTabRef,
    ariaLabel: t('profileLabels.personalInformationTab'),
    role: "tab",
    ariaControls: "personal-panel",
  },
  {
    key: "subscription",
    label: t('profileLabels.subscriptionTab'),
    component: defineAsyncComponent(() => import("./SubscriptionTab.vue")),
    ariaLabel: t('profileLabels.subscriptionTab'),
    role: "tab",
    ariaControls: "subscription-panel",
  },
  {
    key: "settings",
    label: t('estatePlanLabels.settingsAndPreferences'),
    component: defineAsyncComponent(() => import("./SettingsTab.vue")),
    ref: settingsTabRef,
    ariaLabel: t('estatePlanLabels.settingsAndPreferences'),
    role: "tab",
    ariaControls: "settings-panel",
  },
  {
    key: "legal",
    label: t('profileLabels.legalTab'),
    component: defineAsyncComponent(() => import("./LegalTab.vue")),
    ariaLabel: t('profileLabels.legalTab'),
    role: "tab",
    ariaControls: "legal-panel",
  },
]);

// Constants for tab identification to avoid magic strings
const TAB_KEYS = {
  PERSONAL: "personal",
  SETTINGS: "settings",
  SUBSCRIPTION: "subscription",
  LEGAL: "legal",
};

// Get tab key by index with safe array access
const getTabKey = (index) =>
  Array.isArray(tabs.value) && tabs.value[index] ? tabs.value[index].key : undefined;

// Show save button only for tabs that require data persistence
const showSaveBtn = computed(() =>
  [TAB_KEYS.PERSONAL, TAB_KEYS.SETTINGS].includes(getTabKey(activeTab.value))
);

// Handle tab switching and load appropriate data for each tab
const handleTabChange = async (tabKey) => {
  if (tabKey === TAB_KEYS.PERSONAL) {
    await getProfile();
    await getAddress();
  } else if (tabKey === TAB_KEYS.SUBSCRIPTION) {
    if (userStore.loginResponse?.licenses?.length > 0) {
      await getSubscriptionData();
    }
    // Focus on select plan link if available
    nextTick(() => {
      const selectPlanLink = document.getElementById("select-plan-link");
      if (selectPlanLink) {
        selectPlanLink.focus();
      }
    });
  } else if (tabKey === TAB_KEYS.SETTINGS) {
    await getContactPreferences();
    await getProfile();
  }
};

const syncSubscription = async () => {
  await getSubscriptionData();
};

// Fetch user subscription data from backend
const getSubscriptionData = async () => {
  try {
    await profileStore.getSubscription();
  } catch (error) {
    console.error("An error occurred while fetching states:", error);
  }
};

// Watch for tab changes and load appropriate data
watch(activeTab, (newIdx) => {
  handleTabChange(getTabKey(newIdx));
});

// Fetch subscription pricing plans from backend API
const getPlans = async () => {
  try {
    await profileStore.getSubscriptionPricing();
  } catch (error) {
    console.error(
      "An error occurred while fetching subscription plans:",
      error
    );
  }
};

// Load initial tab data when component mounts
onMounted(async () => {
  await getPlans();
  handleTabChange(getTabKey(activeTab.value));
});

// Fetch user contact preferences from backend with error handling
const getContactPreferences = async () => {
  try {
    await profileStore.getContactPreferences();
  } catch (error) {
    console.error("An error occurred while fetching states:", error);
  }
};

// Fetch user profile data from backend with error handling
const getProfile = async () => {
  try {
    await profileStore.getUserProfile();
  } catch (error) {
    console.error("An error occurred while fetching states:", error);
  }
};

// Fetch user address data from backend with error handling
const getAddress = async () => {
  try {
    await userStore.getUserAddress();
  } catch (error) {
    console.error("An error occurred while fetching states:", error);
  }
};

// Handle save action for the active tab
async function onSave() {
  if (getTabKey(activeTab.value) === TAB_KEYS.PERSONAL) {
    // Handle personal info tab - update profile and address (create if doesn't exist)
    const [profileRes, addressRes] = await Promise.all([
      userStore.updateProfile(profileData.value),
      userStore.userAddressData?.id
        ? userStore.updateAddress(
            userStore.userAddressData?.id,
            addressData.value
          )
        : userStore.createAddress({
            ...addressData.value,
            id: userStore.loginUserId,
            country: "USA", // Default country set to USA
          }),
    ]);
    // Refresh data after successful updates
    if (profileRes.success && addressRes.success) {
      getProfile();
      getAddress();
      if (userStore.loginResponse?.licenses?.length <= 0) {
        activeTab.value = tabs.value.findIndex(
          (t) => t.key === TAB_KEYS.SUBSCRIPTION
        );
        profileStore.isSubscriptionModalOpen = true;
      }
    }
  } else if (getTabKey(activeTab.value) === TAB_KEYS.SETTINGS) {
    // Handle settings tab - update profile and contact preferences
    const [profileRes, contactPrefRes] = await Promise.all([
      userStore.updateProfile(profileData.value),
      profileStore.updateContactPreferences(contactData.value),
    ]);
    // Refresh data after successful updates
    if (profileRes.success && contactPrefRes.success) {
      getProfile();
      getContactPreferences();
    }
  }
  // Notify parent component of data changes
  emit("sync");
}

// Handle personal info updates from child component
async function onSavePersonalInfo({ address, profile }, isValid) {
  addressData.value = address;
  profileData.value = profile;
  personalInfoValid.value = isValid.value;
}

// Handle settings updates from child component
async function onSaveSettings({ profile, contact }, isValid) {
  profileData.value = profile;
  contactData.value = contact;
  settingValid.value = isValid.value;
}

function goToPersonalTab() {
  activeTab.value = tabs.value.findIndex((t) => t.key === TAB_KEYS.PERSONAL);
}

defineExpose({
  goToPersonalTab,
});

function goToSubscriptionTab() {
  // Switch to the Subscription tab
  activeTab.value = tabs.value.findIndex((t) => t.key === TAB_KEYS.SUBSCRIPTION);

  // Wait a bit to ensure tab is rendered, then open the modal
  setTimeout(() => {
    profileStore.isSubscriptionModalOpen = true;
  }, 300);
}

const rootContainer = ref(null);

const handleFocusRedirect = () => {
    let nextTab = activeTab.value + 1;
    
    // Check if we've reached the end of the tabs, if so, loop back to the first one
    if (nextTab >= tabs.value.length) {
        nextTab = 0;
    }

    if (rootContainer.value) {
        // Use a timeout to ensure this runs after the default tab behavior
        setTimeout(() => {
             // Find tab headers using a more robust selector (ARIA role)
            const tabHeaders = rootContainer.value.querySelectorAll('[role="tab"]');
            
            if (tabHeaders && tabHeaders[nextTab]) {
                // Set focus to the next tab's header
                tabHeaders[nextTab].focus();
            } else {
                 // Fallback: try finding by class if role selector fails
                 const classHeaders = rootContainer.value.querySelectorAll('.p-tabview-nav-link');
                 if (classHeaders && classHeaders[nextTab]) {
                    classHeaders[nextTab].focus();
                 }
            }
        }, 0);
    }
};
</script>

<style>
.p-tabview-tablist {
  gap: 48px;
  border-bottom: 1px solid#d9d9d9 !important;
}
.p-tabview-tab-title {
  font-family: "JUST Sans", sans-serif !important;
  font-weight: 400 !important;
  font-size: 16px;
  line-height: 100% !important;
  letter-spacing: 0%;
  vertical-align: middle;
  color: #222222;
}
.p-tabview-tablist-item-active .p-tabview-tab-title {
  font-weight: 600 !important;
}
.p-tabview-tablist-item .p-tabview-tab-header {
  border-width: 0 !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
  padding-top: 8px !important;
  padding-bottom: 12px !important;
}
.p-tabview-tablist-item-active .p-tabview-tab-header {
  border-color: #05a8f4 !important;
  border-width: 0 0 2px 0 !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.p-tabview-ink-bar {
  background: #05a8f4 !important;
}
</style>
